// src/components/job-engine/JobDetailPage.tsx
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Stack, Text, PrimaryButton, DefaultButton, MessageBar, MessageBarType, Link as FluentLink, Icon, Pivot, PivotItem } from "@fluentui/react";
import { useJob } from "./useJob";
import { useJobEvents } from "./useJobEvents";
import StatePill from "./StatePill";
import PayloadViewer from "./PayloadViewer";
import JobTimeline from "./JobTimeline";
import LineageGraph from "./LineageGraph";
import { formatDateTime } from "./formatters";
import { getJobChildren, retryJob } from "@/api/jobEngine";
import type { Job } from "@/api/jobEngine";
import { isAxiosError } from "axios";
import useJobEngineEvent from "./useJobEngineEvent";
import { useJobEngineConnectionState } from "./useJobEngineConnectionState";
import ConnectionStatusBadge from "./ConnectionStatusBadge";

const JobDetailPage: React.FC = () => {
  const { jobId = "" } = useParams();
  const navigate = useNavigate();
  const job = useJob(jobId);
  const events = useJobEvents(jobId);
  const [children, setChildren] = useState<Job[]>([]);
  const [childrenError, setChildrenError] = useState<string | null>(null);
  const [status, setStatus] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [actionDisabledUntil, setActionDisabledUntil] = useState<number>(0);

  useEffect(() => {
    if (!jobId) return;
    const ctrl = new AbortController();
    setChildrenError(null);
    getJobChildren(jobId, ctrl.signal)
      .then((list) => { if (!ctrl.signal.aborted) setChildren(list); })
      .catch((err) => {
        if (ctrl.signal.aborted) return;
        console.error("getJobChildren failed", err);
        setChildren([]);
        setChildrenError(err?.message ?? "Failed to load children");
      });
    return () => ctrl.abort();
  }, [jobId]);

  useJobEngineEvent<{ jobId: string }>("JobStarted",  (e) => { if (e.jobId === jobId) { job.refetch(); events.refetch(); } });
  useJobEngineEvent<{ jobId: string }>("JobFinished", (e) => { if (e.jobId === jobId) { job.refetch(); events.refetch(); } });
  useJobEngineEvent<{ jobId: string }>("JobFailed",   (e) => { if (e.jobId === jobId) { job.refetch(); events.refetch(); } });
  useJobEngineEvent<{ jobId: string }>("JobDeadLettered", (e) => { if (e.jobId === jobId) { job.refetch(); events.refetch(); } });

  const onRetry = useCallback(async () => {
    setStatus(null);
    try {
      await retryJob(jobId);
      setStatus({ type: "success", text: "Retry queued" });
      job.refetch();
      events.refetch();
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 403) {
        setStatus({ type: "error", text: "You don't have permission to retry jobs." });
      } else if (isAxiosError(err)) {
        setStatus({ type: "error", text: err.response?.data?.title ?? "Retry failed" });
      } else {
        setStatus({ type: "error", text: "An unexpected error occurred." });
      }
      setActionDisabledUntil(Date.now() + 5000);
      window.setTimeout(() => setActionDisabledUntil(0), 5000);
    }
  }, [jobId, job, events]);

  const isConnected = useJobEngineConnectionState();
  const isActionDisabled = Date.now() < actionDisabledUntil;

  return (
    <div style={{ height: "100%", overflowY: "auto", overflowX: "hidden" }}>
    <Stack tokens={{ childrenGap: 16 }}>
      <div className="page-header" style={{ marginTop: 0 }}>
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 16 }}>
          <span
            role="link"
            tabIndex={0}
            onClick={() => navigate("/job-engine/jobs")}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate("/job-engine/jobs"); }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#f3f2f1"; e.currentTarget.style.color = "#323130"; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "#605e5c"; }}
            style={{
              fontSize: 14,
              color: "#605e5c",
              cursor: "pointer",
              userSelect: "none",
              padding: "4px 10px",
              marginLeft: -10,
              borderRadius: 4,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              transition: "background-color 120ms ease, color 120ms ease",
            }}
          >
            <Icon iconName="ChevronLeft" styles={{ root: { fontSize: 11 } }} />
            Back to jobs
          </span>
          <h2 className="page-title" style={{ margin: 0 }}>Job {jobId}</h2>
        </Stack>
        <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
          {job.data && <StatePill state={job.data.state} />}
          <PrimaryButton text="Retry" onClick={onRetry} disabled={isActionDisabled || job.isLoading || !job.data} />
          <ConnectionStatusBadge isConnected={isConnected} />
        </Stack>
      </div>

      {status && (
        <MessageBar messageBarType={status.type === "success" ? MessageBarType.success : MessageBarType.error}>
          {status.text}
        </MessageBar>
      )}
      {job.error && <MessageBar messageBarType={MessageBarType.error}>{job.error}</MessageBar>}

      {/* Two-tab layout — "Details" carries the existing metadata + timeline +
          payload + error blocks; "Lineage" hosts the React Flow graph and the
          textual parent / children / View-chain links. Splitting reduces
          vertical pressure on the Details view and keeps the graph at a
          comfortable canvas height without crowding the rest of the page. */}
      {job.data && (
        <Pivot styles={{ root: { paddingTop: 12 } }}>
          <PivotItem headerText="Details" itemKey="details">
            <Stack horizontal wrap tokens={{ childrenGap: 16 }} styles={{ root: { paddingTop: 12 } }}>
              <Stack tokens={{ childrenGap: 6 }} styles={{ root: { flex: "1 1 320px" } }}>
                <Text variant="small">Type: {job.data.jobType}</Text>
                <Text variant="small">Correlation: {job.data.correlationId}</Text>
                <Text variant="small">Created: {formatDateTime(job.data.createdAt)}</Text>
                <Text variant="small">Started: {formatDateTime(job.data.startedAt)}</Text>
                <Text variant="small">Finished: {formatDateTime(job.data.finishedAt)}</Text>
                <Text variant="small">Attempts: {job.data.attemptCount}</Text>
              </Stack>
              <Stack tokens={{ childrenGap: 6 }} styles={{ root: { flex: "2 1 360px" } }}>
                <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>Timeline</Text>
                <JobTimeline events={events.items} />
              </Stack>
              <Stack tokens={{ childrenGap: 6 }} styles={{ root: { flex: "2 1 360px" } }}>
                <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>Payload</Text>
                <PayloadViewer value={job.data.payload} />
                {job.data.lastError && (
                  <Stack tokens={{ childrenGap: 4 }}>
                    <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>Last error</Text>
                    <div style={{ fontFamily: "monospace", whiteSpace: "pre-wrap", padding: 8, border: "1px solid #edebe9", borderRadius: 4, background: "#faf9f8" }}>
                      {job.data.lastError}
                    </div>
                  </Stack>
                )}
              </Stack>
            </Stack>
          </PivotItem>

          <PivotItem headerText="Lineage" itemKey="lineage">
            <Stack tokens={{ childrenGap: 8 }} styles={{ root: { paddingTop: 12 } }}>
              {/* Visual descendant DAG — backed by GET /v1/jobs/{jobId}/lineage.
                  Click a node to navigate to that job's detail page. */}
              <LineageGraph rootJobId={jobId} height={500} />

              {job.data.parentJobId && (
                <Stack horizontal tokens={{ childrenGap: 6 }} verticalAlign="center">
                  <Text variant="small">Triggered by:</Text>
                  <FluentLink
                    styles={{ root: { fontSize: 12 } }}
                    onClick={() => navigate(`/job-engine/jobs/${encodeURIComponent(job.data!.parentJobId!)}`)}
                    title={job.data.parentJobId}
                  >
                    {job.data.parentJobId}
                  </FluentLink>
                </Stack>
              )}

              {(children.length > 0 || childrenError) && (
                <Stack tokens={{ childrenGap: 4 }}>
                  <Text variant="small">Triggered ({children.length}):</Text>
                  {childrenError && (
                    <Text variant="small" styles={{ root: { color: "#a4262c" } }}>
                      Couldn't load children — see console for details.
                    </Text>
                  )}
                  {children.map((c) => (
                    <Stack key={c.jobId} horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                      <FluentLink
                        styles={{ root: { fontSize: 12 } }}
                        onClick={() => navigate(`/job-engine/jobs/${encodeURIComponent(c.jobId)}`)}
                        title={c.jobId}
                      >
                        {c.jobId}
                      </FluentLink>
                      <Text variant="small" styles={{ root: { color: "#605e5c" } }}>{c.jobType}</Text>
                      <StatePill state={c.state} />
                    </Stack>
                  ))}
                </Stack>
              )}

              {job.data.correlationId && (
                <DefaultButton
                  text="View chain"
                  styles={{ root: { alignSelf: "flex-start", marginTop: 8 } }}
                  onClick={() => navigate(`/job-engine/jobs?correlationId=${encodeURIComponent(job.data!.correlationId)}`)}
                />
              )}
            </Stack>
          </PivotItem>
        </Pivot>
      )}

    </Stack>
    </div>
  );
};

export default JobDetailPage;
